<template>
  <v-app-bar elevation="1" outlined app class="z-increase" height="75">
    <!-- Logo -->
    <v-toolbar-items>
      <div class="d-flex align-center mr-2">
        <span class="hidden-lg-and-up mr-2">
          <!-- Menu for left navbar items md below -->
          <v-menu left bottom :offset-y="true">
            <template v-slot:activator="{ props }">
              <v-btn icon v-bind="props">
                <v-icon>{{ menuIcon }}</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item
                v-for="item in links"
                :key="item.id + item.text"
                :to="item.to"
                :href="item.href"
                :target="item.href ? '_blank' : ''"
              >
                <v-list-item-icon>
                  <v-icon>{{ item.icon }}</v-icon>
                </v-list-item-icon>
                {{ $t(item.text) }}
                <v-list-item-icon v-if="item.href">
                  <v-icon>{{ externalLinkIcon }}</v-icon>
                </v-list-item-icon>
              </v-list-item>
            </v-list>
          </v-menu>
        </span>
        <span v-if="userOrganization.orgLogoLink" class="hidden-sm-and-down">
          <!-- ORG LOGO -->
          <img
            :src="userOrganization.orgLogoLink"
            height="60"
            style="
              max-width: 150px;
              vertical-align: middle;
              background-color: white;
              border-radius: 4px;
              margin-left: 52px;
              margin-right: 52px;
            "
            :alt="$t('labels.organization') + ' logo'"
          />
        </span>
        <span v-else class="hidden-sm-and-down">
          <!-- EYE LOGO - default when no logo -->
          <v-img
            src="../../assets/eye_logo.png"
            max-height="60"
            max-width="150"
            contain
            :style="{ 'background-color': isDarkmodeEnabled ? 'white' : '' }"
          />
        </span>
      </div>
    </v-toolbar-items>

    <v-toolbar-items class="hidden-md-and-down">
      <!-- Left toolbar items -->
      <v-btn
        text
        class="toolbar-nav-btn"
        v-for="item in links"
        :key="item.text"
        :to="item.to"
        :href="item.href"
        :target="item.href ? '_blank' : ''"
      >
        <v-icon v-if="item.icon" left dark>{{ item.icon }}</v-icon>
        {{ $t(item.text) }}
        <v-icon v-if="item.href" right light>{{ externalLinkIcon }}</v-icon>
      </v-btn>
    </v-toolbar-items>
    <v-spacer class="hidden-sm-and-down" />

    <v-toolbar-items>
      <div class="d-flex align-center">
        <v-select
          :class="`org-select ${selectWidth}`"
          v-if="isSuperAdminOrEyeStaff"
          v-model="organization"
          :items="organizations"
          item-title="name"
          item-value="id"
          :label="$t('labels.organization')"
          variant="solo-filled"
          hide-details
          dense
          solo
          @update:modelValue="onSelectOrg"
        />
      </div>
    </v-toolbar-items>
    <v-spacer />

    <v-toolbar-items>
      <!-- Right toolbar items -->
      <div class="d-flex align-center mr-5">
        <span class="clock" style="color: #2196f3f0">{{ clockTime }}</span>
      </div>
      <div class="hidden-sm-and-down d-md-flex align-center mr-5">
        <!-- Added mr-5 here for spacing -->
        <span>{{ username }}</span>
      </div>
      <v-menu left bottom :offset-y="true">
        <template v-slot:activator="{ props }">
          <v-btn icon v-bind="props">
            <v-icon>{{ menuIcon }}</v-icon>
          </v-btn>
        </template>

        <v-list>
          <v-list-item class="hidden-md-and-up">
            <v-list-item-icon>
              <v-icon color="info">{{ userIcon }}</v-icon>
            </v-list-item-icon>
            <span>{{ username }}</span>
          </v-list-item>
          <v-list-item v-for="item in listItems" :key="item.id" @click="onButtonClick(item.id)">
            <v-list-item-icon>
              <v-icon :color="item.color">{{ item.icon }}</v-icon>
            </v-list-item-icon>
            <span>{{ $t(item.label) }}</span>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-toolbar-items>
    <SelectLangDialog
      dialogTitle="labels.switch_lang"
      dialogSubtitle="labels.select_locale"
      submitBtnLabel="labels.confirm"
      :dialog="isSelectLangOpen"
      :loading="false"
      @close="isSelectLangOpen = false"
      @confirm="(lang) => switchLanguage(lang)"
    />
  </v-app-bar>
</template>

<script setup>
defineOptions({ name: 'AppNavbar' });
import {
  ref, computed, onMounted, onBeforeUnmount,
} from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useDisplay, useTheme } from 'vuetify';
import { getAuth, signOut } from 'firebase/auth';
import {
  mdiLogoutVariant,
  mdiMenu,
  mdiAccountOutline,
  mdiVideoOutline,
  mdiMovieOutline,
  mdiWeatherSunny,
  mdiWeatherNight,
  mdiFileDocument,
  mdiOpenInNew,
  mdiTranslate,
} from '@mdi/js';
import SelectLangDialog from './SelectLangDialog';

const store = useStore();
const { locale } = useI18n();
const { lg } = useDisplay();
const theme = useTheme();

// Reactive state
const isSelectLangOpen = ref(false);
const menuIcon = mdiMenu;
const externalLinkIcon = mdiOpenInNew;
const userIcon = mdiAccountOutline;
const organization = ref(null);

const onSelectOrg = () => {
  // to retain selected organization even user refresh the screen
  localStorage.setItem('organization', organization.value);
  store.dispatch('organizations/setSelectedOrganization');
};

const windowWidth = ref(window.innerWidth);
const clockTime = ref('');
setInterval(() => {
  const now = new Date();
  clockTime.value = now.toLocaleTimeString([], { hour12: false });
}, 1000);

// Navigation links
const links = [
  { text: 'labels.live', to: '/', icon: mdiVideoOutline },
  { text: 'labels.videos', to: '/videos', icon: mdiMovieOutline },
  {
    text: 'labels.procedures',
    href: './procedures-rev2.pdf',
    icon: mdiFileDocument,
  },
  {
    text: 'labels.112_video',
    href: 'https://112.video/',
    icon: mdiVideoOutline,
  },
];

// Computed properties
const username = computed(() => store.state.auth.username);
const isDarkmodeEnabled = computed(() => store.state.userPreferences.darkmode);
const userOrganization = computed(() => store.state.organizations.userOrganization || {});
const organizations = computed(() => store.state.organizations.organizations);
const isSuperAdminOrEyeStaff = computed(() => store.state.auth.claims?.superAdmin || store.state.auth.claims?.eyeStaff);

const listItems = computed(() => [
  {
    icon: isDarkmodeEnabled.value ? mdiWeatherNight : mdiWeatherSunny,
    label: isDarkmodeEnabled.value ? 'labels.disable_night_mode' : 'labels.activate_night_mode',
    color: 'info',
    id: 'darkmode',
  },
  {
    icon: mdiTranslate,
    label: 'labels.language',
    color: 'info',
    id: 'locale',
  },
  {
    icon: mdiLogoutVariant,
    label: 'labels.logout',
    color: 'warning',
    id: 'logout',
  },
]);

const selectWidth = computed(() => {
  if (lg.value) {
    if (windowWidth.value < 1400) {
      return 'max-200';
    }
    if (windowWidth.value < 1500) {
      return 'max-300';
    }
  }
  return 'max-400';
});

// Methods
const onButtonClick = (id) => {
  if (id === 'autoPlay') {
    store.commit('userPreferences/TOGGLE_AUTOPLAY');
  } else if (id === 'locale') {
    isSelectLangOpen.value = true;
  } else if (id === 'darkmode') {
    store.commit('userPreferences/TOGGLE_DARKMODE');
    // Use Vuetify 3's useTheme composable to switch theme
    theme.global.name.value = isDarkmodeEnabled.value ? 'dark' : 'light';
    // Optionally update the html class for custom CSS if needed
    document.querySelector('html').classList.toggle('v-theme--dark', !isDarkmodeEnabled.value);
  } else if (id === 'logout') {
    const auth = getAuth();
    signOut(auth).then(
      () => {
        document.location = '/';
        localStorage.removeItem('organization');
      },
      () => {
        // console.error(err);
      },
    );
  } else if (id === 'resources') {
    window.open('https://storage.googleapis.com/eyenation-prod.appspot.com/pdf/public/ressources-bmt-fr.pdf', '_blank');
  }
};

const switchLanguage = (lang) => {
  locale.value = lang;
  isSelectLangOpen.value = false;
  localStorage.setItem('locale', lang);
};

const onWindowResize = () => {
  windowWidth.value = window.innerWidth;
};

// Lifecycle hooks
// Ensure theme is synced with store on mount (for persisted state)
onMounted(() => {
  theme.global.name.value = isDarkmodeEnabled.value ? 'dark' : 'light';
  // Set language from localStorage
  locale.value = localStorage.getItem('locale') || locale.value;

  // Initialize organizations if needed
  if (!store.state.organizations.isInitialized) {
    store.dispatch('organizations/getOrganizations');
  }

  // Set organization from localStorage if available
  const orgId = localStorage.getItem('organization') ?? null;
  if (orgId) organization.value = orgId;

  // Add window resize event listener
  window.addEventListener('resize', onWindowResize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onWindowResize);
});
</script>

<style lang="scss" scoped>
.z-increase {
  z-index: 210 !important;
}
.clock {
  width: 200px;
  height: 50px;
  font-size: 28px;
  font-family: 'Courier New', Courier, monospace;
  font-weight: bold;
  letter-spacing: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.org-select {
  padding: 0 10px;
  &.max-200 {
    max-width: 200px;
  }
  &.max-300 {
    max-width: 300px;
  }
  &.max-400 {
    max-width: 400px;
  }
}
</style>
